import { InvitationsList, InviteMemberDialog } from '@/components/teams';
import Page, {
  PageHeader,
  PageHeaderActions,
  PageHeaderContent,
  PageHeaderDescription,
  PageHeaderTitle,
} from '@/components/Page';
import { useAuth } from '@/hooks/useAuth';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute, Link } from '@tanstack/react-router';
import { Avatar, AvatarFallback, AvatarImage } from '@ui/components/avatar';
import { Button } from '@ui/components/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@ui/components/card';
import { BookOpen, Settings, UserPlus, Users } from 'lucide-react';
import { useState } from 'react';

export const Route = createFileRoute('/_dashboard/teams/$teamId')({
  component: RouteComponent,
  loader: async ({ params }) => {
    return {
      crumb: `Team ${params.teamId} Details`,
    };
  },
});

function RouteComponent() {
  const { teamId } = Route.useParams();
  const { getFullOrganization } = useAuth();
  const [isInviteDialogOpen, setIsInviteDialogOpen] = useState(false);

  const {
    data: team,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['team', teamId],
    queryFn: () =>
      getFullOrganization({
        query: { organizationId: teamId ?? '' },
      }),
    enabled: !!teamId,
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-muted-foreground">Loading team details...</div>
      </div>
    );
  }

  if (error || !team) {
    return (
      <div className="flex flex-col items-center justify-center h-64 space-y-4">
        <div className="text-destructive">Failed to load team details</div>
        <Button asChild variant="outline">
          <Link to="/teams">Back to Teams</Link>
        </Button>
      </div>
    );
  }

  return (
    <Page>
      <PageHeader>
        <PageHeaderContent>
          <PageHeaderTitle>{team.data?.name}</PageHeaderTitle>
          <PageHeaderDescription>
            <span className="text-xs font-mono bg-muted px-2 py-1 rounded">{team.data?.id}</span>
          </PageHeaderDescription>
        </PageHeaderContent>
        <PageHeaderActions>
          <Button onClick={() => setIsInviteDialogOpen(true)} className="gap-2">
            <UserPlus className="h-4 w-4" />
            Invite Member
          </Button>
          <Button variant="outline" className="gap-2">
            <Settings className="h-4 w-4" />
            Manage Team
          </Button>
        </PageHeaderActions>
      </PageHeader>

      {/* Team Overview Cards */}
      <div className="grid gap-6 md:grid-cols-3">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Admins</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{team.data?.members.length}</div>
            <p className="text-xs text-muted-foreground">Team administrators</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Students</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{0}</div>
            <p className="text-xs text-muted-foreground">Enrolled students</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Courses</CardTitle>
            <BookOpen className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{0}</div>
            <p className="text-xs text-muted-foreground">Available courses</p>
          </CardContent>
        </Card>
      </div>

      {/* Members and Invitations Section */}
      <div className="grid gap-6 md:grid-cols-2">
        {/* Admins */}
        <Card>
          <CardHeader>
            <CardTitle>Admins</CardTitle>
            <CardDescription>Team administrators and their roles</CardDescription>
          </CardHeader>
          <CardContent>
            {team.data?.members && team.data.members.length > 0 ? (
              <div className="space-y-2">
                {team.data.members.map((admin) => (
                  <div
                    key={admin.id}
                    className="flex items-center justify-between p-2 border rounded"
                  >
                    <div className="flex items-center space-x-2">
                      <Avatar className="h-8 w-8 rounded-lg">
                        <AvatarImage src={admin?.user.image ?? ''} alt={admin.user.name} />
                        <AvatarFallback className="rounded-lg">
                          {admin.user.name.substring(0, 2)}
                        </AvatarFallback>
                      </Avatar>
                      <div>
                        <div className="font-medium">{admin.user.name.slice(0, 8)}</div>
                        <div className="text-sm text-muted-foreground">Administrator</div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-4 text-muted-foreground">No admins assigned</div>
            )}
          </CardContent>
        </Card>

        {/* Students */}
        <Card>
          <CardHeader>
            <CardTitle>Students</CardTitle>
            <CardDescription>Students enrolled in this team</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-center py-4 text-muted-foreground">No students enrolled</div>
          </CardContent>
        </Card>
      </div>

      {/* Pending Invitations */}
      {teamId && <InvitationsList organizationId={teamId} />}

      {/* Courses Section */}
      <Card>
        <CardHeader>
          <CardTitle>Courses</CardTitle>
          <CardDescription>Courses available for this team</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8 text-muted-foreground">
            <BookOpen className="h-12 w-12 mx-auto mb-4 opacity-50" />
            <p>No courses available for this team</p>
          </div>
        </CardContent>
      </Card>

      {/* Invite Member Dialog */}
      <InviteMemberDialog
        open={isInviteDialogOpen}
        onOpenChange={setIsInviteDialogOpen}
        organizationId={teamId ?? null}
      />
    </Page>
  );
}
